import React, { useMemo } from 'react';
import { Item, DeliveryCharge, Discount } from '../../types';
import './Totals.css';

interface TotalsProps {
  items: Item[];
  deliveryCharge: DeliveryCharge;
  discount: Discount;
  onDeliveryChargeChange: (charge: DeliveryCharge) => void;
  onDiscountChange: (discount: Discount) => void;
}

const Totals: React.FC<TotalsProps> = ({ 
  items, 
  deliveryCharge, 
  discount,
  onDeliveryChargeChange,
  onDiscountChange
}) => {
  // Inputs remain always editable; propagate changes up to parent immediately.
  const handleDeliveryChargeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updated: DeliveryCharge = {
      ...deliveryCharge,
      [name]: name === 'amount' ? (parseFloat(value) || 0) : value,
    } as DeliveryCharge;
    onDeliveryChargeChange(updated);
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updated: Discount = {
      ...discount,
      [name]: name === 'amount' ? (parseFloat(value) || 0) : value,
    } as Discount;
    onDiscountChange(updated);
  };
  // Calculate subtotal and total using useMemo for performance
  const { subtotal, total } = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const discountAmount = discount.amount;
    const discountedSubtotal = subtotal - discountAmount;
    const total = discountedSubtotal + deliveryCharge.amount;
    
    return { subtotal, total };
  }, [items, discount.amount, deliveryCharge.amount]);

  return (
    <div className="totals-container">
      <div className="totals-header">
        <h3>Receipt Summary</h3>
      </div>
      
      {/* Subtotal */}
      <div className="total-row subtotal">
        <span>Subtotal</span>
        <span>Tk {subtotal.toFixed(2)}</span>
      </div>
      
      {/* Discount */}
      <div className={`total-row discount editing`}>
        <div className="edit-field">
          <input
            type="text"
            name="label"
            value={discount.label}
            onChange={handleDiscountChange}
            className="discount-label-input"
          />
          <span>- Tk</span>
          <input
            type="number"
            name="amount"
            value={discount.amount || 0}
            onChange={handleDiscountChange}
            min="0"
            step="0.01"
            className="discount-amount-input"
          />
        </div>
      </div>
      
      {/* Delivery Charge */}
      <div className={`total-row delivery editing`}>
        <div className="edit-field">
          <input
            type="text"
            name="label"
            value={deliveryCharge.label}
            onChange={handleDeliveryChargeChange}
            className="delivery-label-input"
          />
          <span>Tk</span>
          <input
            type="number"
            name="amount"
            value={deliveryCharge.amount || 0}
            onChange={handleDeliveryChargeChange}
            min="0"
            step="0.01"
            className="delivery-amount-input"
          />
        </div>
      </div>
      
      {/* Grand Total */}
      <div className="total-row grand-total">
        <span>Total</span>
        <span>Tk {total.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default Totals;

